/**
 * Explosive mines (GDD section 8, chest weapon): put or thrown, a mine arms at the next turn. It explodes
 * (radius 9, 40 damage) when a unit passes within 4 cells, or on contact with fire or lava, or
 * when a pistol bullet hits it. It falls with the terrain.
 */
import { Physique, STATE_LIQUID, isqrt } from '@invaders/sim';
import { FP, FP_SHIFT, TICKS_PER_SECOND, type GameState, type Mine } from './state';
import { GRAVITY, MAX_FALL_SPEED, PAD_VY, UNIT_H, UNIT_W, ownerPlayer } from './unit';
import { WEAPONS } from './weapons';
import { WEAPON_ICE_MINE, WEAPON_MINE } from './weapon-ids';
import { freezeArea } from './cold';
import { explode } from './explosion';

/** GDD: it goes off when a unit passes within 4 cells. */
export const MINE_TRIGGER = 4;
/** Sandbox (no turns): armed 2 s after it is put. */
export const MINE_SANDBOX_ARM_TICKS = 2 * TICKS_PER_SECOND;
/** Half size of a mine, for hits and drawing. */
export const MINE_HALF = 2;

export const MINE_EXPLOSIVE = 0;
/** GDD: the ice mine goes off like the explosive one and freezes a radius of 30. */
export const MINE_ICE = 1;

export function placeMine(state: GameState, x: number, y: number, owner: number, kind = MINE_EXPLOSIVE): void {
  const arm = state.match ? state.match.turn + 1 : state.tick + MINE_SANDBOX_ARM_TICKS;
  state.mines.push({ kind, x, y, arm, owner });
}

export function isArmed(state: GameState, mine: Mine): boolean {
  return state.match ? state.match.turn >= mine.arm : state.tick >= mine.arm;
}

/** Distance from cell (x, y) to the nearest cell of a unit's body. */
function distanceToUnit(x: number, y: number, ux: number, uy: number): number {
  const dx = x < ux ? ux - x : x > ux + UNIT_W - 1 ? x - (ux + UNIT_W - 1) : 0;
  const dy = y < uy ? uy - y : y > uy + UNIT_H - 1 ? y - (uy + UNIT_H - 1) : 0;
  return isqrt(dx * dx + dy * dy);
}

function touchesLava(state: GameState, m: Mine): boolean {
  const { w, h, data } = state.world.physique;
  for (let y = Math.max(0, m.y - MINE_HALF - 1); y <= Math.min(h - 1, m.y + MINE_HALF + 1); y++) {
    for (let x = Math.max(0, m.x - MINE_HALF - 1); x <= Math.min(w - 1, m.x + MINE_HALF + 1); x++) {
      const c = data[y * w + x];
      if (c === Physique.LAVA || c === Physique.FIRE) return true;
      // An ice mine does not go off in the cold it makes: only lava and fire set it off.
    }
  }
  return false;
}

export function detonate(state: GameState, index: number): void {
  const m = state.mines[index];
  state.mines.splice(index, 1);
  if (m.kind === MINE_ICE) freezeArea(state, m.x, m.y, WEAPONS[WEAPON_ICE_MINE].radius, WEAPONS[WEAPON_ICE_MINE].damage);
  else explode(state, m.x, m.y, WEAPONS[WEAPON_MINE].radius, WEAPONS[WEAPON_MINE].damage);
}

/** A cell a mine cannot go through: a solid or a powder (outside the map by a side: a wall). */
function holds(state: GameState, x: number, y: number): boolean {
  const { w, h, data } = state.world.physique;
  if (x < 0 || x >= w) return true;
  if (y < 0 || y >= h) return false;
  return state.world.rules.materials.state[data[y * w + x]] < STATE_LIQUID;
}

/**
 * Jump pads and conveyor belts (author's rule, the level editor): a mine at rest on a pad is thrown up, on a belt it is
 * carried along; being moved never sets it off (units near it still do, when it is armed). False once it fell out of the map.
 */
function moveMine(state: GameState, m: Mine): boolean {
  const h = state.world.physique.h;
  if (!m.vy) {
    const below = m.y + MINE_HALF + 1;
    if (!holds(state, m.x, below)) return true;
    for (const p of state.map.pads) {
      if (below !== p.y || m.x < p.x0 || m.x > p.x1) continue;
      m.vy = -PAD_VY;
      m.fy = 0;
      state.events.push({ type: 'pad', unit: -1, x: m.x, y: below });
      break;
    }
    if (!m.vy) {
      const belt = state.map.belts.find((b) => below === b.y && m.x >= b.x0 && m.x <= b.x1);
      if (belt) {
        m.fx = (m.fx ?? 0) + belt.dir * belt.speed;
        while (m.fx >= FP || m.fx <= -FP) {
          const dir = m.fx > 0 ? 1 : -1;
          m.fx -= dir * FP;
          if (holds(state, m.x + dir * (MINE_HALF + 1), m.y)) { m.fx = 0; break; }
          m.x += dir;
        }
      } else if (m.fx) m.fx = 0;
      return m.x >= 0 && m.x < state.world.physique.w;
    }
  }
  // In flight: the gravity of the map, until it lands (or hits a ceiling).
  m.vy = Math.min(MAX_FALL_SPEED, (m.vy ?? 0) + Math.trunc(GRAVITY / state.map.gravityDiv));
  if (m.vy === 0) m.vy = 1; // at the top of its flight: still flying (0 means at rest)
  m.fy = (m.fy ?? 0) + m.vy;
  while (m.fy >= FP) {
    m.fy -= FP;
    if (holds(state, m.x, m.y + MINE_HALF + 1)) { m.vy = 0; m.fy = 0; m.fx = 0; break; }
    m.y++;
  }
  while (m.fy <= -FP) {
    m.fy += FP;
    if (holds(state, m.x, m.y - MINE_HALF - 1)) { m.vy = 0; m.fy = 0; break; }
    m.y--;
  }
  return m.y - MINE_HALF < h;
}

export function updateMines(state: GameState): void {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let i = state.mines.length - 1; i >= 0; i--) {
    const m = state.mines[i];
    if (i >= state.mines.length) continue; // a chain reaction removed some
    if (!moveMine(state, m)) { state.mines.splice(i, 1); continue; }
    // Falls with the terrain: one cell per tick while nothing holds it.
    const below = m.y + MINE_HALF + 1;
    if (below >= h) { state.mines.splice(i, 1); continue; }
    if (!m.vy && matState[data[below * w + m.x]] >= STATE_LIQUID) m.y++;
    let boom = touchesLava(state, m);
    if (!boom && isArmed(state, m)) {
      for (const u of state.units) {
        if (u.alive && distanceToUnit(m.x, m.y, u.x >> FP_SHIFT, u.y >> FP_SHIFT) <= MINE_TRIGGER) {
          boom = true;
          // The match ends the turn of a unit that walks onto an ice mine (author's rule).
          if (m.kind === MINE_ICE) state.events.push({ type: 'iceMine', unit: u.id });
          break;
        }
      }
    }
    if (boom) {
      state.source = ownerPlayer(state, m.owner); // the mine's team (friendly fire)
      detonate(state, i);
      state.source = -1;
    }
  }
}

/** Index of the mine covering cell (x, y), or -1 (for the pistol). */
export function mineAt(state: GameState, x: number, y: number): number {
  return state.mines.findIndex((m) => Math.abs(m.x - x) <= MINE_HALF && Math.abs(m.y - y) <= MINE_HALF);
}
